import { action, observable, runInAction } from 'mobx';
import React from 'react';
import { MessageExitUpgrade, MessageSessionEnding } from '../layouts/BaseLayout/renderers';
import type {
  GlobalModalContent,
  GlobalModalProps,
  GlobalModalState,
} from '../types/global-modal-types';
import AbstractStore from './AbstractStore';
import RootStore from './RootStore';

export const MODAL_ID = 'global-modal';

export default class GlobalModalStore extends AbstractStore {
  /** Volatile modal content (React elements, callbacks). Kept outside observable to avoid MobX proxying and "illegal receiver" errors. See global-modal-types.ts. */
  private modalContent: GlobalModalContent | null = null;

  public constructor(rootStore: RootStore) {
    super(rootStore);
  }

  @observable accessor globalModalState: GlobalModalState = {
    id: MODAL_ID,
    show: false,
    okCaption: '',
    exitOkAction: false,
  };

  /** Bumped when setModalCallbacks runs so observers re-render and get fresh merged props. */
  @observable private accessor modalContentVersion = 0;

  @action
  public showOkCancelPopup(
    header: string | string[],
    children: React.ReactNode,
    okCaption: string,
    cancelCaption: string,
    onOk: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
    closeOnClickOutside = false,
    okAutoHide = true,
    exitOkAction = false,
  ): void {
    const act = async (ok: boolean): Promise<void> => {
      runInAction(() => (this.globalModalState.disabled = true));
      if (ok) {
        await onOk();
      } else if (onCancel) {
        await onCancel();
      }
      runInAction(() => {
        this.globalModalState.disabled = false;
        if (!ok || okAutoHide) this.closeGlobalModal();
      });
    };

    this.modalContent = {
      children,
      onOk: () => void act(true),
      onCancel: () => void act(false),
      onClickOutside: closeOnClickOutside ? () => void act(false) : undefined,
    };
    this.globalModalState = {
      id: MODAL_ID,
      'data-testid': MODAL_ID,
      show: true,
      okCaption,
      cancelCaption,
      header,
      exitOkAction,
    };
  }

  @action
  public showOkPopup(
    header: string | string[],
    children: React.ReactNode,
    onOk?: () => void,
    okCaption?: string,
    options?: { className?: string; exitOkAction?: boolean },
  ): void {
    const act = (): void => {
      runInAction(() => this.closeGlobalModal());
      onOk?.();
    };
    this.modalContent = { children, onOk: act, onClickOutside: onOk ? () => act() : undefined };
    this.globalModalState = {
      id: MODAL_ID,
      'data-testid': MODAL_ID,
      show: true,
      okCaption: okCaption || 'OK',
      header,
      exitOkAction: options?.exitOkAction ?? true,
      ...(options?.className && { className: options.className }),
    };
  }

  @action
  public closeGlobalModal(): void {
    this.globalModalState.show = false;
    this.modalContent = null;
  }

  /** Merged props for the global modal. Content first, then state so observable overrides (e.g. display fields) win. */
  public getGlobalModalProps(): GlobalModalProps {
    void this.modalContentVersion; // track so observers re-render when setModalContent runs
    const modalContent = this.modalContent ?? { onOk: (): void => {} };
    return { ...modalContent, ...this.globalModalState };
  }

  /** Merge callbacks/content into current modal (for flows that set onOk/onCancel/customFooter after open). */
  public setModalContent(callbacks: Partial<GlobalModalContent>): void {
    if (this.modalContent) {
      this.modalContent = { ...this.modalContent, ...callbacks };
      runInAction(() => this.modalContentVersion++);
    }
  }

  public showOkCancelPopupAsync(
    header: string | string[],
    children: React.ReactNode,
    okCaption: string,
    cancelCaption: string,
    onOk: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
    closeOnClickOutside = false,
    okAutoHide = true,
    exitOkAction = false,
  ): Promise<void> {
    return new Promise(resolve =>
      this.showOkCancelPopup(
        header,
        children,
        okCaption,
        cancelCaption,
        async () => {
          await onOk?.();
          resolve();
        },
        async () => {
          await onCancel?.();
          resolve();
        },
        closeOnClickOutside,
        okAutoHide,
        exitOkAction,
      ),
    );
  }

  public showOkPopupAsync(
    header: string | string[],
    children: React.ReactNode,
    onOk?: () => void,
    okCaption?: string,
  ): Promise<void> {
    return new Promise(resolve =>
      this.showOkPopup(
        header,
        children,
        () => {
          onOk?.();
          resolve();
        },
        okCaption,
      ),
    );
  }

  public showExitUpgradePopup(onOk: () => void): void {
    this.showOkCancelPopup(
      'Exit Upgrade?',
      React.createElement(MessageExitUpgrade),
      'Save and Exit',
      'Return to Upgrade',
      onOk,
    );
  }

  public showSessionEndingPopup(): void {
    this.showOkCancelPopup(
      'Session Ending',
      React.createElement(MessageSessionEnding),
      'Extend Session',
      'End Session',
      () => this.root.globalStore.extendUpgradeTaskSessionLock(),
      () => void this.root.globalStore.navigate('/'),
    );
  }
}
